/**
 * What the signed-in person may do - the screens' side of the permissions
 * (the server's is App\Support\Permissions, and it is the one that counts:
 * every add, edit and delete is checked there again).
 *
 *   admin     everything
 *   customer  the customer portal, exactly as before
 *   staff     anybody else: the ticks an admin gave them, per screen -
 *             "orders.view", "orders.delete", "containers.post", ...
 *
 * The ticks come from GET /v2/me, read at sign-in and again whenever the
 * app opens, so a change an admin makes reaches the person on their next
 * page load without signing out.
 */

import { createContext, useContext, useEffect, useState } from "react";
import axios from "axios";

const ME = "https://trust-cargo.co/anas_backend/api/v2/me";
const KEY = "permissions";
const CHANGED = "trust-permissions-changed";

const storage = () => {
  try {
    return window.sessionStorage;
  } catch (e) {
    return null;
  }
};

export const roleOf = () => storage()?.getItem("role_id") || "";
export const isAdmin = () => roleOf() === "admin";
export const isCustomer = () => roleOf() === "customer";
/** Signed in, and neither the admin nor a customer. */
export const isStaff = () => Boolean(roleOf()) && !isAdmin() && !isCustomer();

export const myPermissions = () => {
  try {
    const list = JSON.parse(storage()?.getItem(KEY) || "[]");
    return Array.isArray(list) ? list : [];
  } catch (e) {
    return [];
  }
};

/** Whether I hold any one of the permissions ("orders.delete" or a list). */
export const can = (keys) => {
  if (isAdmin()) return true;
  const mine = myPermissions();
  return [].concat(keys).some((k) => mine.includes(k));
};

export const setPermissions = (list) => {
  storage()?.setItem(KEY, JSON.stringify(Array.isArray(list) ? list : []));
  window.dispatchEvent(new Event(CHANGED));
};

/** Read my permissions from the server; keeps what was there if it cannot. */
export async function loadPermissions() {
  const token = storage()?.getItem("token");
  if (!token || token === "undefined") return myPermissions();
  try {
    const res = await axios.get(ME, {
      headers: { Accept: "application/json", Authorization: `Bearer ${token}` },
    });
    setPermissions(res.data?.permissions || []);
  } catch (e) {
    // an old server without /me: nothing for staff, the admin is unaffected
  }
  return myPermissions();
}

/** Re-renders when the permissions are read again. */
export function usePermissionsVersion() {
  const [version, setVersion] = useState(0);
  useEffect(() => {
    const bump = () => setVersion((v) => v + 1);
    window.addEventListener(CHANGED, bump);
    return () => window.removeEventListener(CHANGED, bump);
  }, []);
  return version;
}

/* ------------------------------------------------------------------ */
/* pages                                                                */
/* ------------------------------------------------------------------ */

/** Each staff page and what opening it needs. */
export const PAGES = {
  "/users": "users.view",
  "/add_user": "users.create",
  "/edit_user/:id": "users.update",
  "/customers": "customers.view",
  "/add_customer": "customers.create",
  "/edit_customer/:customer_id": "customers.update",
  "/notifications": "notifications.view",
  "/shipping-mark": "shipping_mark.view",
  "/settings": "notification_settings.view",
  "/containers": "containers.view",
  "/view_container/:id": "containers.view",
  "/container_orders/:id": "containers.view",
  "/add_container": "containers.create",
  "/edit_container/:id": "containers.update",
  "/orders": "orders.view",
  "/loaded_orders": "orders.view",
  "/all_orders": "orders.view",
  "/view_order/:id": "orders.view",
  "/add_order": "orders.create",
  "/edit_order/:id": "orders.update",
  "/edit_order_data/:id": "orders.update",
  "/recive_goods": "orders.update",
  "/payments": "payments.view",
  "/customer_payments": "payments.view",
  "/suppliers": "suppliers.view",
  "/customer_suppliers": "suppliers.view",
  "/vendors": "vendors.view",
  "/accounting": "accounting.view",
  "/posts": "posts.view",
  "/add_post": "posts.create",
  "/messages": "messages.view",
  "/add_message": "messages.create",
  "/edit_message/:id": "messages.update",
  "/status": "status.view",
  "/add_status": "status.create",
  "/edit_status/:id": "status.update",
  "/backups": "backups.view",
  "/audit-log": "audit_log.view",
};

/** Pages everybody signed in has: the start page and their own account. */
const OPEN = [
  "/dashboard",
  "/homescreen",
  "/profile",
  "/edit_current_user",
  "/edit_current_user_password",
  "/authentication/sign-in",
  "/authentication/sign-up",
];

/** The screen a page belongs to - "orders" for /view_order/:id. */
export const moduleOf = (path) => (PAGES[path] || "").split(".")[0] || null;

/**
 * Whether I may open a page. The admin may open anything and a customer
 * what the portal always let them; staff need the page's permission, and
 * a page not on the list is the admin's.
 */
export const canOpen = (path) => {
  if (!roleOf() || isAdmin() || isCustomer()) return true;
  if (OPEN.includes(path)) return true;
  return Boolean(PAGES[path]) && can(PAGES[path]);
};

/** The main pages, in the menu's order - where staff may start. */
export const MAIN_PAGES = [
  "/users",
  "/customers",
  "/notifications",
  "/shipping-mark",
  "/settings",
  "/containers",
  "/orders",
  "/loaded_orders",
  "/payments",
  "/suppliers",
  "/vendors",
  "/accounting",
  "/posts",
  "/messages",
  "/status",
  "/backups",
  "/audit-log",
];

/** Where to land after signing in: staff on the first main page they may open. */
export const startPage = (menu = MAIN_PAGES.map((route) => ({ route }))) => {
  if (isAdmin()) return "/users";
  if (isCustomer()) return "/messages";
  const first = menu.find((r) => r.route && PAGES[r.route] && canOpen(r.route));
  return first ? first.route : "/homescreen";
};

/* ------------------------------------------------------------------ */
/* the screen a page is - for its buttons                               */
/* ------------------------------------------------------------------ */

export const PermissionScope = createContext(null);

/**
 * Whether I may do `action` ("create", "update", "export", ...) on the
 * screen I am on. Outside any screen's scope, or for the admin or a
 * customer, nothing is hidden.
 */
export function useCan() {
  usePermissionsVersion();
  const module = useContext(PermissionScope);
  return (action, onModule = module) => !onModule || !isStaff() || can(`${onModule}.${action}`);
}
